import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  Chip,
  alpha,
} from '@mui/material';
import { useState } from 'react';

import { AlertCircleIcon } from '@/assets/icons/AlertCircleIcon';
import { CalendarIcon } from '@/assets/icons/CalendarIcon';
import { Home03Icon } from '@/assets/icons/Home03Icon';
import { MessageCircle01Icon } from '@/assets/icons/MessageCircle01Icon';
import { PiggyBank02Icon } from '@/assets/icons/PiggyBank02Icon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { Settings01Icon } from '@/assets/icons/Settings01Icon';
import { Star01Icon } from '@/assets/icons/Star01Icon';
import { Tool01Icon } from '@/assets/icons/Tool01Icon';
import { Users02Icon } from '@/assets/icons/Users02Icon';

interface CommunityCalendarFirstTimeExperienceProps {
  onCreateEvent: () => void;
  canCreateEvent: boolean;
  isUserMember: boolean;
}

interface EventExample {
  icon: React.ReactNode;
  title: string;
  description: string;
  category: string;
  color: string;
}

const eventExamples: EventExample[] = [
  {
    icon: <Users02Icon width={24} height={24} />,
    title: 'Community Meetings',
    description: 'Monthly HOA meetings and resident gatherings',
    category: 'Meeting',
    color: 'blue',
  },
  {
    icon: <Tool01Icon width={24} height={24} />,
    title: 'Maintenance Schedule',
    description: 'Track scheduled repairs and maintenance work',
    category: 'Maintenance',
    color: 'orange',
  },
  {
    icon: <Star01Icon width={24} height={24} />,
    title: 'Social Events',
    description: 'BBQs, holiday parties, and community celebrations',
    category: 'Social',
    color: 'purple',
  },
  {
    icon: <AlertCircleIcon width={24} height={24} />,
    title: 'Important Deadlines',
    description: 'Payment due dates and compliance deadlines',
    category: 'Deadline',
    color: 'red',
  },
  {
    icon: <Home03Icon width={24} height={24} />,
    title: 'Property Inspections',
    description: 'Annual inspections and property walkthroughs',
    category: 'Inspection',
    color: 'green',
  },
  {
    icon: <Settings01Icon width={24} height={24} />,
    title: 'Service Schedules',
    description: 'Trash pickup, landscaping, and cleaning schedules',
    category: 'Service',
    color: 'yellow',
  },
];

const CommunityCalendarFirstTimeExperience: React.FC<CommunityCalendarFirstTimeExperienceProps> = ({
  onCreateEvent,
  canCreateEvent,
  isUserMember,
}) => {
  const [selectedExample, setSelectedExample] = useState<string | null>(null);

  const getEventColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: '#2196f3',
      orange: '#ff9800',
      purple: '#9c27b0',
      red: '#f44336',
      green: '#4caf50',
      yellow: '#ffeb3b',
    };
    return colors[color] || '#9e9e9e';
  };

  return (
    <Stack spacing={4} sx={{ py: 2 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: 'center' }}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Box sx={{ color: 'primary.main' }}>
            <CalendarIcon width={48} height={48} />
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Community Calendar
          </Typography>
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.900', mb: 2 }}>
          Keep your community organized and informed
        </Typography>
        <Typography variant="body1" sx={{ color: 'secondary.700', maxWidth: 600, mx: 'auto' }}>
          The Community Calendar is your central hub for scheduling events, tracking important
          dates, and ensuring everyone stays up-to-date with community activities.
        </Typography>
      </Box>

      {/* Benefits Section */}
      <Card
        elevation={0}
        sx={{
          bgcolor: 'secondary.50',
          border: '1px solid',
          borderColor: 'secondary.200',
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: 'secondary.900', mb: 3, textAlign: 'center' }}
          >
            Why Use the Community Calendar?
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                  }}
                >
                  <CalendarIcon width={28} height={28} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Stay Organized
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Never miss important dates or community events
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                  }}
                >
                  <Users02Icon width={28} height={28} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Increase Participation
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Make it easy for residents to attend events
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                  }}
                >
                  <MessageCircle01Icon width={28} height={28} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Improve Communication
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Keep everyone informed about community activities
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                  }}
                >
                  <AlertCircleIcon width={28} height={28} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Reduce Conflicts
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Avoid scheduling conflicts and double bookings
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Event Types */}
      <Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: 'secondary.900', mb: 3, textAlign: 'center' }}
        >
          Types of Events You Can Add
        </Typography>
        <Grid container spacing={2}>
          {eventExamples.map((example) => (
            <Grid item xs={12} sm={6} md={4} key={example.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor:
                    selectedExample === example.title ? 'primary.main' : 'divider',
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  height: '100%',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  },
                }}
                onClick={() => setSelectedExample(example.title)}
              >
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1.5,
                        bgcolor: alpha(getEventColor(example.color), 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: getEventColor(example.color),
                      }}
                    >
                      {example.icon}
                    </Box>
                    <Chip
                      label={example.category}
                      size="small"
                      sx={{
                        bgcolor: alpha(getEventColor(example.color), 0.1),
                        color: getEventColor(example.color),
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Stack>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: 'secondary.900', mb: 0.5 }}
                    >
                      {example.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'secondary.600', lineHeight: 1.5 }}>
                      {example.description}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Features */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'primary.200',
          bgcolor: 'primary.50',
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: 'secondary.900', mb: 3, textAlign: 'center' }}
        >
          Calendar Features
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  📅 Multiple Views
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Switch between month, week, and day views to see events how you prefer
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  🔄 Recurring Events
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Set up weekly, monthly, or custom recurring schedules
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  🔔 Event Reminders
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Get notified about upcoming events and important dates
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  🏷️ Event Categories
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Organize events by type with color-coded categories
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  📍 Location Details
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Add venue information and directions to events
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  💬 Event Descriptions
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Include detailed information and instructions for attendees
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Integration Note */}
      <Stack direction="row" spacing={3} sx={{ justifyContent: 'center', flexWrap: 'wrap' }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'secondary.200',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: { xs: '100%', sm: 'auto' },
          }}
        >
          <Box sx={{ color: 'primary.main' }}>
            <MessageCircle01Icon width={32} height={32} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Linked to Community Hub
            </Typography>
            <Typography variant="body2" sx={{ color: 'secondary.600' }}>
              Events can be linked to posts and discussions
            </Typography>
          </Box>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'secondary.200',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: { xs: '100%', sm: 'auto' },
          }}
        >
          <Box sx={{ color: 'success.main' }}>
            <PiggyBank02Icon width={32} height={32} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Save Together Integration
            </Typography>
            <Typography variant="body2" sx={{ color: 'secondary.600' }}>
              Track group negotiation deadlines
            </Typography>
          </Box>
        </Paper>
      </Stack>

      {/* CTA Section */}
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.900', mb: 2 }}>
          Ready to organize your community?
        </Typography>
        {isUserMember ? (
          <>
            <Button
              startIcon={<PlusIcon />}
              variant="contained"
              size="large"
              onClick={onCreateEvent}
              disabled={!canCreateEvent}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.75,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.25)',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: '0px 6px 16px rgba(157, 21, 0, 0.35)',
                },
                '&.Mui-disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.500',
                },
              }}
            >
              Add Your First Event
            </Button>
            <Typography variant="body2" sx={{ color: 'secondary.600', mt: 2 }}>
              Start building a more connected and organized community
            </Typography>
          </>
        ) : (
          <Typography variant="body1" sx={{ color: 'secondary.700' }}>
            Join the community to start adding events to the calendar
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

export default CommunityCalendarFirstTimeExperience;